import classNames from "classnames";

import type { PaymentSource } from "../../../hooks/useBilling";
import type { Estimate } from "../../../hooks/useCheckout";
import type { SeatBasedTeamSubscription } from "../../../hooks/useTeamsTypes";
import useTranslation from "../../../hooks/useTranslation";
import { ChargebeePaymentType } from "../../../lib/ChargebeeAdapter";
import { formatDueOnDate } from "../../../utils/convertUtils";
import { CheckoutSummaryRow } from "../../ChargebeeCheckout/components/CheckoutSummaryRow/CheckoutSummaryRow";
import { EstimateSummaryPeriod } from "../EstimateSummaryPeriod/EstimateSummaryPeriod";

import style from "./AddSeatsEstimateSummary.module.css";

const DEFAULT_ESTIMATE_AMOUNT = "0.00";

export const AddSeatsEstimateSummary: React.FC<{
  proratedEstimate?: Estimate;
  renewalEstimate: Estimate;
  renewalDate: string;
  currencySymbol: string;
  isLoading: boolean;
  additionalEditorSeatCount: number;
  subscription: SeatBasedTeamSubscription;
  paymentSource: PaymentSource;
  submitButtonText?: string;
}> = ({
  proratedEstimate,
  renewalEstimate,
  renewalDate,
  currencySymbol,
  isLoading,
  additionalEditorSeatCount,
  subscription,
  paymentSource,
  submitButtonText,
}) => {
  const { t, m } = useTranslation();
  const resolvedSubmitButtonText = submitButtonText ?? t(m.EstimateSummary.BuySeats);
  const trialEnd = renewalEstimate.trialEnd;
  const isTrialSummaryLayout = subscription.isTrial && Boolean(trialEnd);
  const extraEditorSeatsOnRenewal = renewalEstimate.editorSeatCount - 1;

  return (
    <div>
      <div className={style.summary}>
        <span className={style.summaryTitle}>{t(m.EstimateSummary.Summary)}</span>

        {isTrialSummaryLayout && trialEnd && (
          <>
            <CheckoutSummaryRow
              name={t(m.EstimateSummary.DueOnWithTax, { date: formatDueOnDate(trialEnd) })}
              price={renewalEstimate?.totalAmount ?? DEFAULT_ESTIMATE_AMOUNT}
              currencySymbol={currencySymbol}
              isLoading={isLoading}
            />
            <div className={classNames(style.tertiary, style.priceBreakdown)}>
              {extraEditorSeatsOnRenewal > 0 && (
                <>
                  <span>
                    {t(m.EstimateSummary.EditorSeatBreakdown, {
                      count: extraEditorSeatsOnRenewal,
                      price: `${currencySymbol}${renewalEstimate?.unitAmount}`,
                    })}
                  </span>
                  {" × "}
                  <EstimateSummaryPeriod subscriptionPeriod={renewalEstimate?.period} />
                  <br />
                </>
              )}
              <span>
                {t(m.EstimateSummary.OwnerSeatBreakdown, {
                  price: `${currencySymbol}${renewalEstimate?.unitAmount}`,
                })}
              </span>
              {" × "}
              <EstimateSummaryPeriod subscriptionPeriod={renewalEstimate?.period} />
            </div>
            <div className={style.trialWrapper}>
              <hr className={style.horizontalLine} />
              <CheckoutSummaryRow
                isPriceHighlighted={true}
                name={t(m.EstimateSummary.TotalDueToday)}
                price={proratedEstimate?.totalAmount ?? DEFAULT_ESTIMATE_AMOUNT}
                currencySymbol={currencySymbol}
              />
            </div>
          </>
        )}
        {!isTrialSummaryLayout && (
          <>
            <CheckoutSummaryRow
              name={t(m.EstimateSummary.DueTodayWithTax)}
              priceTooltipText={t(m.EstimateSummary.PriceProratedTooltip)}
              price={proratedEstimate?.totalAmount ?? DEFAULT_ESTIMATE_AMOUNT}
              currencySymbol={currencySymbol}
              isLoading={isLoading}
            />
            <div className={classNames(style.tertiary, style.priceBreakdown)}>
              <span>
                {t(m.EstimateSummary.EditorSeatBreakdown, {
                  count: additionalEditorSeatCount || 1,
                  price: `${currencySymbol}${renewalEstimate?.unitAmount}`,
                })}
              </span>
              {" × "}
              <EstimateSummaryPeriod
                subscriptionPeriod={renewalEstimate?.period}
                partialDurationMs={new Date(renewalDate).getTime() - Date.now()}
              />
            </div>
          </>
        )}
      </div>

      {!renewalEstimate.trialEnd && (
        <p className={classNames(style.tertiary, style.additionalInfo)}>
          {paymentSource.type === ChargebeePaymentType.Card && (
            <span>
              {t(m.EstimateSummary.CardChargedOnClick, {
                lastFourDigits: paymentSource.lastFourDigits,
                submitButtonText: resolvedSubmitButtonText,
              })}
            </span>
          )}
          {paymentSource.type === ChargebeePaymentType.ApplePay && (
            <span>
              {t(m.EstimateSummary.ApplePayChargedOnClick, {
                submitButtonText: resolvedSubmitButtonText,
              })}
            </span>
          )}
          <br />
          <span>{t(m.EstimateSummary.AllSeatsRenewAtFullPrice)}</span>{" "}
          <span>{t(m.EstimateSummary.CardChargedRenewal, { period: renewalEstimate.period })}</span>{" "}
          <span>
            {t(m.EstimateSummary.NextPaymentAmount, {
              amount: `${currencySymbol}${renewalEstimate.totalAmount}`,
            })}
          </span>
          {renewalEstimate.currencyCode === "USD" && (
            <>
              {" "}
              <span>
                {t(m.EstimateSummary.AllPricesIn, { currencyCode: renewalEstimate.currencyCode })}
              </span>
            </>
          )}
        </p>
      )}
      {renewalEstimate.trialEnd && (
        <p className={classNames(style.tertiary, style.additionalInfo)}>
          <span>
            {t(m.EstimateSummary.TrialEndsOn, {
              date: formatDueOnDate(renewalEstimate.trialEnd),
            })}
          </span>{" "}
          <span>{t(m.EstimateSummary.AllSeatsRenewAtFullPrice)}</span>{" "}
          {paymentSource.type === ChargebeePaymentType.Card && (
            <>
              <span>
                {t(m.EstimateSummary.CardChargedAfterTrial, {
                  lastFourDigits: paymentSource.lastFourDigits,
                  period: renewalEstimate.period,
                })}
              </span>{" "}
            </>
          )}
          {paymentSource.type === ChargebeePaymentType.ApplePay && (
            <>
              <span>
                {t(m.EstimateSummary.ApplePayChargedAfterTrial, {
                  period: renewalEstimate.period,
                })}
              </span>{" "}
            </>
          )}
          <span>
            {t(m.EstimateSummary.NextPaymentAmount, {
              amount: `${currencySymbol}${renewalEstimate.totalAmount}`,
            })}
          </span>
          {renewalEstimate.currencyCode === "USD" && (
            <>
              {" "}
              <span>
                {t(m.EstimateSummary.AllPricesIn, { currencyCode: renewalEstimate.currencyCode })}
              </span>
            </>
          )}
        </p>
      )}
    </div>
  );
};
